<template lang="pug">
div
    div(
        class="relative group"
    )
        div(
            class="absolute z-10 w-full top-0 bottom-0 justify-between items-center pointer-events-none transition-opacity"
            :class="arrowModeClass"
        )
            button(
                v-if="canScroll"
                class="py-6 px-2 pointer-events-auto"
                :class="arrowButtonClass.left"
                @click="scrollLeft"
            )
                ThinArrow(v-if="arrowStyle === 'THIN'" class="w-5")
                ChevronLeftIcon(v-else class="w-6 h-6")
            button(
                v-if="canScroll"
                class="py-6 px-2 pointer-events-auto"
                :class="arrowButtonClass.right"
                @click="scrollRight"
            )
                ThinArrow(v-if="arrowStyle === 'THIN'" class="w-5 rotate-180")
                ChevronRightIcon(v-else class="w-6 h-6")

        div(
            class="carousel-items flex flex-nowrap overflow-x-scroll lg:overflow-x-hidden"
            :class="containerClass"
            ref="items"
        )
            slot

</template>

<script>
import ChevronRightIcon from '@/assets/common/chevronRight?inline'
import ChevronLeftIcon from '@/assets/common/chevronLeft?inline'
import ThinArrow from '@/assets/carousel-arrow?inline'

export default {
    name: 'Carousel',
    components: { ChevronRightIcon, ChevronLeftIcon, ThinArrow },
    props: {
        arrowMode: {
            type: String,
            default: 'HOVER',
            validator: (value) => {
                return value === 'NONE' || value === 'HOVER' || value === 'ALWAYS'
            },
        },
        isLooped: {
            type: Boolean,
            default: false,
        },
        containerClass: {
            type: String,
            default: '',
        },
        scrollTo: {
            type: Number,
            default: 0,
        },
        arrowStyle: {
            type: String,
            default: 'DEFAULT',
            validator: (value) => {
                return value === 'DEFAULT' || value === 'THIN'
            },
        },
    },

    data() {
        return {
            scrolledLeft: true,
            scrolledRight: false,
            canScroll: true,
        }
    },

    computed: {
        arrowModeClass() {
            if (this.arrowMode === 'NONE') return 'hidden'
            else if (this.arrowMode === 'ALWAYS') return 'flex'

            // Default 'HOVER' state
            return 'hidden opacity-0 lg:flex lg:group-hover:opacity-100'
        },
        arrowButtonClass() {
            const baseCarouselClasses = 'bg-white text-black shadow-md md:shadow-none'
            return {
                left: [
                    this.scrolledLeft && !this.isLooped ? 'opacity-25' : 'opacity-90',
                    this.arrowStyle === 'DEFAULT' ? `rounded-r ${baseCarouselClasses}` : '',
                ],
                right: [
                    this.scrolledRight && !this.isLooped ? 'opacity-25' : 'opacity-90',
                    this.arrowStyle === 'DEFAULT' ? `rounded-l ${baseCarouselClasses}` : '',
                ],
            }
        },
    },

    watch: {
        scrollTo(newIndex, oldIndex) {
            const speed = Math.abs(oldIndex - newIndex) > 1 ? 0 : 500

            this.scroll(this.$refs.items.children[newIndex], speed)
        },
    },

    mounted() {
        this.checkCanScroll()

        this.$refs.items?.addEventListener('scroll', () => {
            this.checkScrollPosition()
        })
    },

    methods: {
        scrollLeft() {
            if (!this.isLooped && this.scrolledLeft) return

            if (!this.$refs.items || this.$refs.items.children.length === 0) return

            const children = this.$refs.items.children

            if (this.isLooped && this.$refs.items.scrollLeft === 0) {
                this.scroll(children[children.length - 1])
                return
            }

            this.scroll(this.getScrollTarget(false))
        },
        scrollRight() {
            if (!this.isLooped && this.scrolledRight) return

            if (!this.$refs.items || this.$refs.items.children.length === 0) return

            const children = this.$refs.items.children

            if (
                this.isLooped &&
                this.$refs.items.scrollLeft + this.$refs.items.clientWidth ===
                    this.$refs.items.scrollWidth
            ) {
                this.scroll(children[0])
                return
            }

            this.scroll(this.getScrollTarget())
        },
        scroll(target, speed = 500) {
            const options = {
                container: this.$refs.items,
                x: true,
                y: false,
                force: true,
                easing: 'ease-in-out',
            }

            this.$scrollTo(target, speed, options)
        },
        // Checks right scroll by default
        getScrollTarget(scrollingRight = true) {
            let firstClipped

            for (let i = 0; i < this.$refs.items.children.length; i++) {
                const elementBoundingRect = this.$refs.items.children[i].getBoundingClientRect()
                const parentBoundingRect =
                    this.$refs.items.children[i].offsetParent.getBoundingClientRect()

                if (scrollingRight && elementBoundingRect.right > parentBoundingRect.right) {
                    firstClipped = this.$refs.items.children[i]
                    break
                } else if (
                    !scrollingRight &&
                    elementBoundingRect.left <= parentBoundingRect.left &&
                    elementBoundingRect.left >= parentBoundingRect.left - parentBoundingRect.width
                ) {
                    firstClipped = this.$refs.items.children[i]
                    break
                }
            }

            return firstClipped
        },
        checkScrollPosition() {
            if (!this.$refs.items) return

            this.scrolledLeft = false
            this.scrolledRight = false

            // Scrolled all the way to the left
            if (this.$refs.items.scrollLeft === 0) {
                this.scrolledLeft = true
                return
            }

            // Scrolled all the way to the right
            if (
                this.$refs.items.scrollLeft ===
                this.$refs.items.scrollWidth - this.$refs.items.offsetParent.clientWidth
            ) {
                this.scrolledRight = true
            }
        },
        checkCanScroll() {
            if (!this.$refs.items || this.$refs.items.children.length === 0) return

            const lastItem = this.$refs.items.lastElementChild

            if (!lastItem.offsetParent) return

            const lastItemBounds = lastItem.getBoundingClientRect()
            const parentBounds = lastItem.offsetParent.getBoundingClientRect()

            this.canScroll = lastItemBounds.right > parentBounds.right
        },
    },
}
</script>

<style lang="stylus" scoped>
.carousel-items
    scroll-snap-type: x mandatory

    > *
        scroll-snap-align: start
        flex: none


@screen lg
    .carousel-items
        scroll-snap-type: none

        > *
            scroll-snap-align: none
</style>
